import { apiGet, apiPost, apiPatch, apiDelete } from "./client";
import type { Application } from "@career-ops-amplify/shared";
import type { ApplicationQuery } from "@/types";

export interface ListResponse {
  applications: Application[];
  total: number;
}

export interface CreateApplicationInput {
  company: string;
  role: string;
  score?: number;
  jobUrl?: string;
  jobDescription?: string;
  location?: string;
  notes?: string;
  status?: string;
}

/** List applications with optional filtering / pagination. */
export async function listApplications(
  params?: ApplicationQuery,
): Promise<ListResponse> {
  return apiGet<ListResponse>("/api/applications", params as Record<string, string | number | boolean | undefined>);
}

/** Fetch a single application by number. */
export async function getApplication(num: number): Promise<Application> {
  return apiGet<Application>(`/api/applications/${num}`);
}

/** Create a new application (auto-assigns next number). */
export async function createApplication(
  input: CreateApplicationInput,
): Promise<Application> {
  const res = await apiPost<{ application: Application }>("/api/applications", input);
  return res.application;
}

/** Update any fields on an application (full merge). */
export async function updateApplication(
  n: number,
  updates: Partial<Application>,
): Promise<void> {
  await apiPatch(`/api/applications/${n}`, updates);
}

/** Update the status (and optionally notes) of an application. */
export async function updateStatus(
  n: number,
  status: string,
  notes?: string,
): Promise<Application> {
  return apiPatch<Application>(`/api/applications/${n}/status`, {
    status,
    notes,
  });
}

/** Delete an application from the tracker. */
export async function deleteApplication(n: number): Promise<void> {
  return apiDelete<void>(`/api/applications/${n}`);
}

/** Get the evaluation report for an application. */
export async function getReport(n: number): Promise<{ content: string }> {
  return apiGet<{ content: string }>(`/api/applications/${n}/report`);
}